// src/pages/AcceptCollectionTransfer.jsx
//
// Page publique : le client clique sur le lien dans l'email.
// Si connecté avec le bon email → acceptation directe.
// Si non connecté → redirection vers login/register avec retour automatique.
// Si pending_signup → acceptation post-inscription via AuthContext.
//
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

export default function AcceptCollectionTransfer() {
  const { token } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState("loading"); // loading | preview | accepting | done | error | wrong_user
  const [request, setRequest] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // ── Charger les infos du transfert (sans auth) ──
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token) { setStatus("error"); setErrorMsg(t("acceptCollectionTransfer.errors.invalid")); return; }
    loadRequest();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadRequest = async () => {
    try {
      const { data, error } = await supabase
        .from("collection_transfer_requests")
        .select("id, to_email, collection_ids, status, expires_at, message, advisor_id, created_at")
        .eq("token", token)
        .maybeSingle();

      if (error || !data) { setStatus("error"); setErrorMsg(t("acceptCollectionTransfer.errors.notFound")); return; }
      if (data.status === "accepted") { setStatus("done"); setRequest(data); return; }
      if (data.status === "expired" || new Date(data.expires_at) < new Date()) {
        setStatus("error"); setErrorMsg(t("acceptCollectionTransfer.errors.expired")); return;
      }
      if (data.status === "cancelled") { setStatus("error"); setErrorMsg(t("acceptCollectionTransfer.errors.cancelled")); return; }

      // Charger les titres des collections
      const { data: cols } = await supabase
        .from("collections")
        .select("id, title")
        .in("id", data.collection_ids);

      setRequest({ ...data, collections: cols || [] });
      setStatus("preview");
    } catch (e) {
      setStatus("error"); setErrorMsg(t("acceptCollectionTransfer.errors.network"));
    }
  };

  // ── Vérifier que l'utilisateur connecté correspond au destinataire ──
  useEffect(() => {
    if (status === "preview" && user && request) {
      if (user.email.toLowerCase() !== request.to_email.toLowerCase()) {
        setStatus("wrong_user");
      }
    }
  }, [status, user, request]);

  const handleAccept = async () => {
    if (!user) return;
    setStatus("accepting");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/accept-collection-transfer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ token }),
        }
      );
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || t("acceptCollectionTransfer.errors.generic"));
      setStatus("done");
    } catch (e) {
      setStatus("error"); setErrorMsg(e.message);
    }
  };

  // ── Pas encore connecté : on redirige vers login avec returnTo ──
  const handleLoginRedirect = () => {
    navigate(`/login?returnTo=/accept-transfer/${token}`);
  };
  const handleRegisterRedirect = () => {
    navigate(`/register?email=${encodeURIComponent(request?.to_email || "")}&returnTo=/accept-transfer/${token}`);
  };

  // ─────── RENDER ───────

  if (status === "loading") return <Wrapper><p style={sSub}>{t("acceptCollectionTransfer.loading")}</p></Wrapper>;

  if (status === "error") return (
    <Wrapper>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <h1 style={sTitle}>{t("acceptCollectionTransfer.invalidTitle")}</h1>
        <p style={sSub}>{errorMsg}</p>
        <Link to="/" style={sBtnSecondary}>{t("acceptCollectionTransfer.home")}</Link>
      </div>
    </Wrapper>
  );

  if (status === "wrong_user") return (
    <Wrapper>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
        <h1 style={sTitle}>{t("acceptCollectionTransfer.wrongUserTitle")}</h1>
        <p style={sSub}>{t("acceptCollectionTransfer.wrongUserBody", { expected: request?.to_email, current: user?.email })}</p>
        <p style={{ fontSize: 13, color: "#888", fontFamily: "system-ui", marginBottom: 20 }}>
          {t("acceptCollectionTransfer.wrongUserHelp")}
        </p>
        <button onClick={() => supabase.auth.signOut().then(() => handleLoginRedirect())} style={sBtnPrimary}>
          {t("acceptCollectionTransfer.switchAccount")}
        </button>
      </div>
    </Wrapper>
  );

  if (status === "done") return (
    <Wrapper>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 42, marginBottom: 12 }}>✅</div>
        <h1 style={sTitle}>{t("acceptCollectionTransfer.doneTitle")}</h1>
        <p style={sSub}>{t("acceptCollectionTransfer.doneBody")}</p>
        <button onClick={() => navigate("/dashboard")} style={sBtnPrimary}>
          {t("acceptCollectionTransfer.viewCollections")}
        </button>
      </div>
    </Wrapper>
  );

  if (status === "accepting") return (
    <Wrapper><p style={sSub}>{t("acceptCollectionTransfer.accepting")}</p></Wrapper>
  );

  // status === "preview"
  return (
    <Wrapper>
      <div style={{ maxWidth: 480, width: "100%" }}>
        <p style={{ fontSize: 12, color: "#c9a961", fontFamily: "system-ui", letterSpacing: 1, marginBottom: 8 }}>
          {t("acceptCollectionTransfer.eyebrow")}
        </p>
        <h1 style={sTitle}>{t("acceptCollectionTransfer.title")}</h1>

        {request?.message && (
          <blockquote style={{
            borderLeft: "3px solid #c9a961", paddingLeft: 14, margin: "16px 0",
            fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: 14, color: "#555",
          }}>
            {request.message}
          </blockquote>
        )}

        <div style={{ margin: "20px 0" }}>
          <p style={sLabel}>{t("acceptCollectionTransfer.included")}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {request?.collections?.map((col) => (
              <div key={col.id} style={{
                padding: "10px 14px", border: "1px solid #e0d8cc", borderRadius: 6,
                fontFamily: "Georgia, serif", fontSize: 14, color: "#1a1a1a",
              }}>
                {col.title}
              </div>
            ))}
          </div>
        </div>

        <div style={{
          padding: "12px 14px", background: "#fafaf8", border: "1px solid #e0d8cc",
          borderRadius: 6, marginBottom: 24, fontSize: 12, color: "#555", fontFamily: "system-ui",
        }}>
          {t("acceptCollectionTransfer.notice")}
        </div>

        {/* Cas 1 : connecté avec le bon email */}
        {user && user.email.toLowerCase() === request?.to_email.toLowerCase() && (
          <button onClick={handleAccept} style={{ ...sBtnPrimary, width: "100%" }}>
            {t("acceptCollectionTransfer.accept")}
          </button>
        )}

        {/* Cas 2 : pas connecté */}
        {!user && (
          <div>
            <p style={{ ...sSub, marginBottom: 16 }}>
              {t("acceptCollectionTransfer.loginHelp", { email: request?.to_email })}
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleLoginRedirect} style={{ ...sBtnPrimary, flex: 1 }}>
                {t("acceptCollectionTransfer.login")}
              </button>
              <button onClick={handleRegisterRedirect} style={{ ...sBtnSecondary, flex: 1 }}>
                {t("acceptCollectionTransfer.register")}
              </button>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}

function Wrapper({ children }) {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "40px 24px", background: "#f9f8f5",
    }}>
      <div style={{ marginBottom: 32 }}>
        <span style={{ fontFamily: "Georgia, serif", fontSize: 18, color: "#1a1a1a", letterSpacing: 1 }}>
          ARTKIV
        </span>
      </div>
      {children}
    </div>
  );
}

const sTitle = { fontFamily: "Georgia, serif", fontSize: 22, color: "#1a1a1a", margin: "0 0 10px" };
const sSub = { fontSize: 14, color: "#666", fontFamily: "system-ui", margin: "0 0 16px" };
const sLabel = { fontSize: 11, color: "#aaa", fontFamily: "system-ui", marginBottom: 8, letterSpacing: 0.5 };
const sBtnPrimary = {
  padding: "11px 24px", background: "#1a1a1a", color: "#fff",
  border: "none", borderRadius: 4, fontSize: 14,
  fontFamily: "system-ui", fontWeight: 600, cursor: "pointer",
};
const sBtnSecondary = {
  padding: "11px 24px", background: "#fff", color: "#555",
  border: "1px solid #ccc", borderRadius: 4, fontSize: 14,
  fontFamily: "system-ui", fontWeight: 600, cursor: "pointer",
};
